import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Music, ExternalLink } from 'lucide-react';
import { TrackSkeleton } from '../components/LoadingSkeleton';
import { api } from '../api';

export default function ArtistDetail() {
  const { spotifyId } = useParams();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.getArtist(spotifyId)
      .then((data) => {
        setArtist(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load artist:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [spotifyId]);

  if (loading) {
    return (
      <div className="artist-detail" data-testid="artist-detail-loading">
        <div style={{ maxWidth: 1280, margin: '0 auto', paddingTop: '2rem' }}>
          <div className="skeleton" style={{ width: 120, height: 16, marginBottom: '2rem' }} />
          <div style={{ display: 'flex', gap: '3rem' }}>
            <div className="skeleton" style={{ width: 320, height: 320, borderRadius: 20 }} />
            <div style={{ flex: 1 }}>
              <div className="skeleton" style={{ width: '60%', height: 48, marginBottom: '1rem' }} />
              <div className="skeleton" style={{ width: '40%', height: 20, marginBottom: '1.5rem' }} />
              <div className="skeleton" style={{ width: 160, height: 44 }} />
            </div>
          </div>
          <div style={{ marginTop: '3rem' }}>
            <div className="skeleton" style={{ width: 200, height: 28, marginBottom: '1.5rem' }} />
            {Array.from({ length: 5 }).map((_, i) => <TrackSkeleton key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  if (error || !artist) {
    return (
      <div className="artist-detail">
        <div style={{ maxWidth: 1280, margin: '0 auto', paddingTop: '2rem' }}>
          <Link to="/artists" className="back-link">
            <ArrowLeft size={16} /> Back to Artists
          </Link>
          <div className="empty-state">
            <Music size={48} />
            <h3>Artist not found</h3>
            <p>{error || 'This artist could not be loaded.'}</p>
          </div>
        </div>
      </div>
    );
  }

  const tracks = artist.top_tracks || [];

  return (
    <div className="artist-detail" data-testid="artist-detail-page">
      {/* Hero */}
      <div className="artist-hero">
        <div className="artist-hero-image">
          {artist.image_url ? (
            <img src={artist.image_url} alt={artist.name} />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--bg-surface)',
            }}>
              <Music size={64} color="var(--text-muted)" />
            </div>
          )}
        </div>

        <div className="artist-hero-info">
          <Link to="/artists" className="back-link">
            <ArrowLeft size={16} /> Back to Artists
          </Link>
          <h1>{artist.name}</h1>

          <div className="artist-hero-meta">
            <div className="meta-item">
              <Music size={16} />
              {tracks.length} {tracks.length === 1 ? 'track' : 'tracks'}
            </div>
          </div>

          <div className="artist-hero-actions">
            <a
              href={artist.spotify_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-spotify"
              data-testid="open-spotify-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
              Open in Spotify
            </a>
          </div>
        </div>
      </div>

      {/* Track List */}
      {tracks.length > 0 && (
        <div className="tracks-section">
          <h2>Top Tracks</h2>
          <div className="track-list">
            {tracks.map((track, index) => (
              <div
                className="track-item"
                key={track.track_id || index}
                data-testid={`track-${index}`}
              >
                <div className="track-number">{index + 1}</div>
                <div className="track-info">
                  <div className="track-name">{track.name}</div>
                </div>
                {track.spotify_url && (
                  <a
                    href={track.spotify_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="track-spotify-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink size={12} /> Spotify
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Spotify Embed */}
      {artist.embed_url && (
        <div className="embed-section">
          <h2>Listen Now</h2>
          <div className="spotify-embed">
            <iframe
              src={`${artist.embed_url}?theme=0`}
              width="100%"
              height="352"
              frameBorder="0"
              allowFullScreen
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              title={`${artist.name} Spotify Player`}
              style={{ borderRadius: 16 }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
